import { CoverTypeValue, PaymentFrequency, PaymentFrequencySingleton } from '@general/js/enums';
import { ErrorStrings } from '@general/js/constants';
import { convertToCurrencyString } from '@general/js/utils';

/**
 * Checks whether the given string is empty.
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string is undefined, null or has length 0. False otherwise.
 */
export const isEmptyString = (str) => {
  return str === undefined || str === null || str.length === 0;
};

/**
 * Generates a display string for the number of pagination results currently shown. Use with pagination only.
 *
 * @param {number} pageNumber The current page number.
 * @param {number} maxPageSize The maximum number of items allowed per page.
 * @param {number} currentPageSize The actual number of items for the current page.
 * @param {number} totalResultsSize The total number of items across all pages.
 * @param {string} itemName The type of item.
 * @return {string} - Return string of text for pagination.
 */
export const getPaginationDisplayLabel = (pageNumber, maxPageSize, currentPageSize, totalResultsSize, itemName) => {
  // Example: Showing 1-10 of 63 quotes
  return `Showing ${pageNumber * maxPageSize - maxPageSize + 1} - ${pageNumber * maxPageSize - maxPageSize + currentPageSize} of ${totalResultsSize} ${itemName}`;
};

/**
 * Function that takes a string and formats it into sentence case.
 *
 * @param {String} str - String that should be formatted
 * @return {String} - Return string in sentence case (if non-empty), else returns original str
 */
export const sentenceCase = (str) => {
  if (!isEmptyString(str)) {
    // Convert it all to lowercase to start clean.
    // Then, upper case the first letter of the string.
    return str
      .toLowerCase()
      .replace(/[a-z]/i, (letter) => {
        return letter.toUpperCase();
      })
      .trim();
  }
  return str;
};

/**
 * Function that takes a string and converts and capitalises all words.
 * If str attribute is undefined, it will return empty string.
 *
 * @param {String|undefined} str - Take either string or undefined. If string, convert it to capitalised string.
 * @param {Regex} delimiterRegex - Regex to detect split the passed string. Default to /[\s,]+/.
 * @return {String} - Return string of capitalised text or empty string.
 */
export const titleCase = (str, delimiterRegex = '[\\s,]+') => {
  // Convert it all to lowercase to start clean.
  if (!isEmptyString(str)) {
    let baseString = str.toLowerCase();

    // Separate by default space and comma delimiters, or custom if provided.
    const capitaliseList = baseString.split(new RegExp(delimiterRegex));

    // For each work, find it in the original string and swap it out with a capitalised version
    capitaliseList.map((toMatch) => {
      // Remove any native regex characters out of the  specific word.
      const escapedToMatch = toMatch.replace(/[.*+\-?^${}()|[\]\\!]/g, '');

      baseString = baseString
        // `\b` before and after ensures that only whole words are replaced.
        .replace(new RegExp(`\\b${escapedToMatch}\\b`), (toReplace) => {
          return toReplace.replace(/[a-z]/i, (firstLetter) => {
            return firstLetter.toUpperCase();
          });
        })
        .trim();

      return baseString;
    });

    return baseString.trim();
  }
  return '';
};

/**
 * Function that takes the three attributes and returns a formatted string in the complete form of "<Suburb>, <STATE> <postcode>".
 * If any attributes are not passed in, it will be skipped in the format order.
 *
 * @param {String|undefined} suburb - String passed as suburb.
 * @param {String|undefined} state - String passed as state.
 * @param {String|undefined} postcode - String passed as postcode.
 * @return {String} - The formatted version of locality details.
 */
export const getAddressLocalityText = (suburb, state, postcode) => {
  let displayText = '';

  if (suburb) {
    displayText += titleCase(suburb);
  }

  if (suburb && state) {
    displayText += ` ${state.toUpperCase()}`;
  } else if (state) {
    displayText += state.toUpperCase();
  }

  if ((suburb || state) && postcode) {
    displayText += ` ${postcode}`;
  } else if (postcode) {
    displayText += postcode;
  }

  return displayText;
};

/**
 * Function that takes the six attributes and returns a full formated address as String in the complete form of "<address line1> <address line2> <address line3> <Suburb>, <STATE> <postcode>".
 * If any attributes are not passed in, it will be skipped in the format order.
 *
 * @param {String|undefined} line1 - String passed as address line1
 * @param {String|undefined} line2 - String passed as address line2
 * @param {String|undefined} line3 - String passed as address line3
 * @param {String|undefined} suburb - String passed as suburb.
 * @param {String|undefined} state - String passed as state.
 * @param {String|undefined} postcode - String passed as postcode.
 * @return {String} - The formatted version of address details.
 */
export const getFullAddressText = (line1, line2, line3, suburb, state, postcode) => {
  const addressLocalityText = getAddressLocalityText(`${suburb},`, state, postcode);
  let address = '';

  if (!isEmptyString(line1) && line1.trim() !== '') {
    address = `${line1} `;
  }

  if (!isEmptyString(line2) && line2.trim() !== '') {
    address += `${line2} `;
  }

  if (!isEmptyString(line3) && line3.trim() !== '') {
    address += `${line3} `;
  }

  if (!isEmptyString(addressLocalityText) && addressLocalityText.trim() !== '') {
    address += `${addressLocalityText}`;
  }
  return address;
};

/**
 * Function that takes the three attributes and returns a formatted string in the complete form of "Firstname Middlename Lastname".
 * If any attributes are not passed in or passed as undefined, it will be skipped in the format order.
 *
 * @param {String|undefined} firstName - String passed as first name.
 * @param {String|undefined} middleName - String passed as middle name.
 * @param {String|undefined} lastName - String passed as last name.
 * @return {String} - Full name with each name in title case.
 */
export const getFullName = (firstName = undefined, middleName = undefined, lastName = undefined) => {
  return [firstName, middleName, lastName]
    .filter((name) => {
      // Filter out any undefined attributes
      return name !== undefined;
    })
    .map((name) => {
      // Capitalise each string
      return titleCase(name.trim());
    })
    .join(' ') // Join with space to one string
    .trim()
    .replace('  ', ' '); // trim and remove any double spaces to single space
};

/**
 * Function that takes three attributes and returns a formatted string of 'Lastname, FM'. If no first name is provided it will return "Lastname". If no middle name is provided it will return "Lastname, F".
 *
 * @param {String} lastName - String passed as last name. This is required.
 * @param {String|undefined} firstName - String passed as first name. This is can be null, undefined or blank string `''`.
 * @param {String|undefined} middleName - String passed as middle name. This is can be null, undefined or blank string `''`.
 * @return {String} - Formatted string of 'Lastname, FM'
 */
export const getProviderDisplayName = (lastName, firstName = undefined, middleName = undefined) => {
  if (isEmptyString(firstName)) {
    // if there's no firstName, only display lastName
    return titleCase(lastName);
  }
  return `${titleCase(lastName)}, ${firstName[0].toUpperCase()}${isEmptyString(middleName) ? '' : middleName[0].toUpperCase()}`;
};

export const getDisplayMode = (displayMode) => {
  const validDisplayModes = [undefined, 'full', 'condensed'];
  if (!validDisplayModes.includes(displayMode)) {
    throw new Error(`Invalid display mode ${displayMode}, must be one of ${validDisplayModes}`);
  }
  return displayMode || 'full';
};

/**
 * Function that remove '~' from imageUrl which is fetched from BE
 * The imageUrl normally starts with '~', so need to remove "~" to make image loading property
 * @param {String} imageUrl, the image url comes from BE
 */

export const getImageUrl = (imageUrl) => {
  if (isEmptyString(imageUrl)) {
    return '';
  }
  return imageUrl.replace(/~/i, '');
};

/**
 * Returns a whitespace string if string is empty, otherwise returns the original string
 * @param {String} str - Any string
 * @returns {String}
 */
export const returnWhiteSpaceIfEmptyStr = (str) => {
  if (isEmptyString(str)) {
    return ' ';
  }
  return str;
};

/**
 * Returns formatted phone number
 * @param {*} string full 10 digit phone number
 * @returns  - If number starts with 04 return 04## ### ###
 *           - else if other number, returns ## #### ####
 *           - if not defined, returns empty string
 */
export const formatPhoneNumber = (number) => {
  if (number) {
    const num = number.toString().replace(/\s/g, '');
    // '04' is reserved for regional code for mobile number in Australia
    if (num.startsWith('04')) {
      return `${num.slice(0, 4)} ${num.slice(4, 7)} ${num.slice(7)}`;
    }
    return `${num.slice(0, 2)} ${num.slice(2, 6)} ${num.slice(6)}`;
  }
  return '';
};

/**
 * Gets the full product name from list of products
 * @param {array} products array of products containing objects with properties 'name' and 'type'
 * @returns product name (if there are two products, combine name with 'hospital' & 'extras')
 */
export const getProductName = (products) => {
  if (products) {
    if (products.length === 1) {
      return products[0].name;
    }
    if (products.length === 2) {
      const hospitalProduct = products.find((product) => {
        return product.type.toLowerCase() === CoverTypeValue.HOSPITAL_ONLY;
      });
      const extrasProduct = products.find((product) => {
        return product.type.toLowerCase() === CoverTypeValue.EXTRAS_ONLY;
      });
      if (hospitalProduct && extrasProduct) {
        return `${hospitalProduct.name} & ${extrasProduct.name}`;
      }
    }
  }
  return '';
};

/**
 * This function performs token replacement on a given string.
 * @param {String} str The string on which to perform the replacement
 * @param {String | Regex} token The string/regex to be searched for and replaced
 * @param {String} replace The data to replace the `token` with
 * @returns The new string with tokens replaced, otherwise original str if it fails.
 */
export const replaceTokenInString = (str, token, replace) => {
  try {
    if (token instanceof RegExp) {
      return str.replaceAll(token, replace);
    }
    return str.replace(token, replace);
  } catch (e) {
    logger.error('Error in replaceTokenInString', e);
    // return original str in case it fails
    return str;
  }
};

/**
 * This function converts frequency to singleton so that it can be used in  `$premiumAmount / frequency` format. eg. $500 / month. Conversion: Fortnightly -> fortnight, Monthly -> month, Quarterly -> quarter, Semi-annual -> 6 months, Annually -> annual
 * @param {String} frequency Frequency to convert. eg. Fortnightly -> fortnight
 * @returns String of singleton frequency.
 */
export const mapPaymentFrequencyFromRecurringToSingleton = (frequency, getLowerCase = true) => {
  let newFrequency;

  switch (frequency?.toLowerCase()) {
    case PaymentFrequency.FORTNIGHTLY.toLowerCase():
      newFrequency = PaymentFrequencySingleton.FORTNIGHTLY;
      break;
    case PaymentFrequency.MONTHLY.toLowerCase():
      newFrequency = PaymentFrequencySingleton.MONTHLY;
      break;
    case PaymentFrequency.QUARTERLY.toLowerCase():
      newFrequency = PaymentFrequencySingleton.QUARTERLY;
      break;
    case PaymentFrequency.SEMI_ANNUAL.toLowerCase():
      newFrequency = PaymentFrequencySingleton.SEMI_ANNUAL;
      break;
    case PaymentFrequency.ANNUAL.toLowerCase():
      newFrequency = PaymentFrequencySingleton.ANNUAL;
      break;
    default:
      newFrequency = '';
  }

  return getLowerCase ? newFrequency.toLowerCase() : newFrequency;
};

/**
 * This function takes amount and frequency and returns a formatted string to display the premium amount / payment frequency
 * @param {Number | string} premiumAmount The amount to show
 * @param {string} frequency Frequency to show (monthly, quarterly, etc)
 * @returns Returns string formatted as $amt / frequency (e.g. $1500.00 / year)
 */
export const formatToDisplayCurrencyPeriod = (premiumAmount, frequency) => {
  const paymentFrequency = mapPaymentFrequencyFromRecurringToSingleton(frequency); // convert 'monthly' to 'month'

  let formattedPremiumAmount;
  if (typeof premiumAmount === 'number') {
    formattedPremiumAmount = convertToCurrencyString(premiumAmount);
  } else {
    formattedPremiumAmount = `$${premiumAmount}`;
  }

  return `${formattedPremiumAmount} / ${paymentFrequency}`;
};

/**
 * Generates a display string for the number of filtered results in the RecordsTable. To be used with the resultsLabel slot.
 *
 *
 * @param {Number} numOfResults Number of results to show
 * @param {String} category The filter category
 * @returns Returns display string with the total number of filtered results
 */
export const getResultsLabel = (numOfResults, category, additionalCategory) => {
  return `${numOfResults} result${numOfResults > 1 ? 's' : ''} for ${category}${additionalCategory ? `, ${additionalCategory}` : ''}`;
};

/**
 * Generates the submitPolicyInProcessHeader based on the provided flowName.
 *
 * @param {string} flowName - The name of the flow (e.g., from `this.$route.params.flowName`).
 * @param {boolean} submitPolicyInProcess - Indicates if the policy is in process.
 * @returns {string | null} - The generated submitPolicyInProcessHeader or null if not applicable.
 */
export const generateSubmitPolicyInProcessHeader = (flowName, submitPolicyInProcess) => {
  if (!submitPolicyInProcess) {
    return null;
  }

  return ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.replacePlaceholder(
    ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.HEADER,
    flowName || ''
  );
};

/**
 * Returns masked credit card number for display in Review and Submit screen
 * @param {String} maskedPan Returned from IPSI. Can be variable length from 16-19 digits
 * @returns {String} Returns the masked pan in the format of XXXX XXXX XXXX XXXX where last 4 digits are shown and length of mask is equal to maskedPan and grouped into 4s
 */
export const convertIpsiMaskedPanToCreditCardNumberDisplay = (maskedPan) => {
  if (maskedPan) {
    const last4Digits = maskedPan.slice(maskedPan.length - 4);
    const maskedDigits = maskedPan.slice(0, maskedPan.length - 4).replace(/./g, '*');
    const fullPan = maskedDigits + last4Digits;

    // Format the masked digits into groups of 4
    const formattedMaskedPan = fullPan.replace(/(.{4})/g, '$1 ').trim();
    return `${formattedMaskedPan}`;
  }
  return '';
};
